import React, { useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import { motion } from 'framer-motion';
import '../Styles/ExperienceTimeline.css';

const experiences = [
    {
        title: "Software Engineer",
        company: "Credible Data",
        location: "Remote",
        period: "June 2025 – March 2026",
        responsibilities: [
            "Architected and shipped a 0-to-1 agent-based AI system using Python, FastAPI, LangGraph, RAG, MCP, and Vertex AI to automate semantic model generation and refinement end-to-end, achieving 90% faster modeling workflows.",
            "Designed production-grade MCP infrastructure for distributed multi-agent execution, enabling reliable tool invocation across asynchronous agent pipelines and improving orchestration efficiency by 50%.",
            "Built a structured evaluation and testing framework with OpenTelemetry instrumentation to measure agent accuracy, latency, and tool invocation correctness, reducing manual debugging effort by 40%.",
            "Engineered the data platform behind the AI system, automating Cloud SQL to BigQuery CDC pipelines with GCP Datastream and reusable Terraform modules, accelerating infrastructure provisioning by 60%.",
            "Enabled 4+ production database integrations by designing a secure multi-database connectivity architecture across Postgres, BigQuery, Snowflake, and Trino to support distributed query orchestration."
        ],
        skills: ["Python", "Java", "FastAPI", "LangGraph", "LangChain", "RAG", "MCP", "Vertex AI", "GCP", "BigQuery", "Terraform", "OpenTelemetry", "PostgreSQL", "Snowflake", "Trino", "Docker", "Distributed Systems"]
    },
    {
        title: "Software Development Engineer II",
        company: "University of Colorado, Boulder",
        location: "Remote",
        period: "March 2024 – May 2025",
        responsibilities: [
            "Modernized VerbNet website, increasing performance by 20% and resolving critical bugs to enhance user engagement.",
            "Reduced backend load times by 30% by integrating Python (Flask), MongoDB, HTML/CSS, JavaScript, and optimized query execution."
        ],
        skills: ["Python", "Flask", "HTML", "CSS", "JavaScript", "MongoDB","Git"]
    },
    {
        title: "Course Manager - Object Oriented Analysis and Design",
        company: "University of Colorado, Boulder",
        location: "Boulder, CO",
        period: "Aug 2024 – May 2025",
        responsibilities: [
            "Designed and managed course assignments, provided detailed solutions, and assessed student performance to ensure academic excellence.",
            "Conducted lectures, facilitated discussions, and offered guidance to support student understanding and success in object-oriented programming"
        ],
        skills: ["Object Oriented Proramming", "Java", "Design Patterns", "Git"]
    },
    {
        title: "Software Development Intern",
        company: "Karsun Solutions LLC",
        location: "Herndon, VA",
        period: "June 2024 – Aug 2024",
        responsibilities: [
            "Engineered a scalable microservice for PDF and image processing using Java, REST API, AWS, Spring Boot, Hibernate, JPA, Docker, JUnit, GitHub Copilot, and PostgreSQL, improving document processing speed by 50% and accuracy by 30%.",
            "Developed interactive frontend features for Redux App Pilot, a VS Code Extension, using React, TypeScript, Tailwind, Unit testing, AWS Bedrock and Jest, improving UI response times by 25%.",
            "Automated deployment pipelines using Jenkins and Kubernetes, reducing manual administrative tasks by 90%.",
            "Automated user access management for the O'Reilly platform using Google Appsheets and Appscript, reducing the approval process time by 70%."
        ],
        skills: ["Java", "Spring Boot", "PostgreSQL", "React", "TypeScript", "REST API", "AWS", "AWS Bedrock", "Docker", "JUnit", "Jenkins", "Kubernetes", "GitHub Copilot", "Git"]
    },
    {
        title: "Software Engineer II",
        company: "SLB",
        location: "Coimbatore, India",
        period: "January 2023 – July 2023",
        responsibilities: [
            "Designed and optimized backend REST APIs in Java and Spring Boot, improving dashboard load times by 30% for 1,000+ users.",
            "Optimized data processing efficiency by 50% through advanced SQL query optimization and configuration tuning, enhancing application responsiveness and reducing latency.",
            "Designed and maintained scalable ETL pipelines, ensuring 99% uptime for critical operations."
        ],
        skills: ["Java", "Spring Boot", "SQL Server", "MySQL", "REST API", "ETL", "CI/CD", "Azure DevOps", "Agile Development"]
    },
    {
        title: "Software Engineer I",
        company: "SLB",
        location: "Coimbatore, India",
        period: "August 2021 – December 2022",
        responsibilities: [
            "Resolved 200+ critical issues across data workflows and back-end operations using Python, MySQL, and CI/CD, improving system stability and reliability.",
            "Contributed to backend development and data pipeline maintenance, helping achieve a 95% customer satisfaction score.",
            "Reduced manual debugging time by 25% by identifying recurring failure patterns and building reusable Python-based logging and automation tools for backend workflows."
        ],
        skills: ["Python", "MySQL", "CI/CD", "Azure DevOps", "Data Pipelines", "Agile Development"]
    },
    {
        title: "Test Automation Intern",
        company: "Resideo Home Technologies",
        location: "Remote",
        period: "January 2021 – July 2021",
        responsibilities: [
            "During my Automation Testing Internship, I was involved in changing the testing processes with the implementation of Core Java technologies and BDD frameworks like Cucumber. This reduced testing time by 40% and increased test coverage by 25%.",
           "My work significantly improved the reliability and speed of the testing phases, contributing to the overall project success."
        ],
        skills: ["Java", "Object Oriented Programming", "Android Studio", "Appium", "Git", "Cucumber"]
    }
];

function supportsHover() {
    return window.matchMedia('(hover: hover)').matches;
}

function ExperienceTimeline() {
    const [hoverSupported, setHoverSupported] = useState(true);

    useEffect(() => {
        setHoverSupported(supportsHover());
        const mediaQuery = window.matchMedia('(hover: hover)');
        const handler = (e) => setHoverSupported(e.matches);
        mediaQuery.addListener(handler);
        return () => mediaQuery.removeListener(handler);
    }, []);

    return (
        <section id="experience" className="experience-section">
            <Container>
                <motion.h2 
                    className="section-title"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    viewport={{ once: true }}
                >
                    Professional Experience
                </motion.h2>
                <div className="timeline">
                    {experiences.map((exp, index) => (
                        <motion.div 
                            className="timeline-item"
                            key={index}
                            initial={{ opacity: 0, y: 50 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: index * 0.2 }}
                            viewport={{ once: true }}
                        >
                            <div className="timeline-dot"></div>
                            <div className="timeline-content">
                                <h3>{exp.title}</h3>
                                <h4>{exp.company}</h4>
                                <h5>{exp.location}</h5>
                                <p className="period">{exp.period}</p>
                                <div className={hoverSupported ? "hover-content" : ""}>
                                    <div className="responsibilities">
                                        <h6>Key Responsibilities:</h6>
                                        <ul>
                                            {exp.responsibilities.map((resp, i) => (
                                                <li key={i}>{resp}</li>
                                            ))}
                                        </ul>
                                    </div>
                                    <div className="experience-skills-container">
                                        <h6>Technologies & Skills:</h6>
                                        <div className="skills-list">
                                            {exp.skills.map((skill, i) => (
                                                <span key={i} className="skill-tag">{skill}</span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </Container>
        </section>
    );
}

export default ExperienceTimeline;
